import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  ListSubheader,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaArrowLeft, FaCheckCircle, FaDollarSign } from 'react-icons/fa';
import { MdCancel as CancelIcon } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import axiosInstance from 'Services/APIService';
import * as yup from 'yup';
import {
  sellAsset,
  clearSellAssetResponse,
  type SellAssetPayload
} from 'slices/fundManagementInvestment/simulationDashboardSlice';
import {
  getAllAssets,
  GetAllAssetsPayload
} from 'slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import { RootState } from 'app/store';

interface PrefilledAsset {
  assetId: string;
  assetName: string;
  assetType: string;
  currentValue: number;
  purchaseValue: number;
  purchaseDate?: string;
  subType?: string;
}

interface SellIncomeAssetsProps {
  onBack: () => void;
  prefilledAsset?: PrefilledAsset;
  fromAssetList?: boolean;
}

interface SellAssetFormData {
  assetId: string;
  assetName: string;
  sellValue: number;
  deductions: number;
  finalPrice: number;
}

interface Asset {
  assetId: string;
  assetName: string;
  assetType: string;
  currentValue: number;
  purchaseValue?: number;
  purchaseDate?: string;
  subType?: string;
}

const validationSchema = yup.object({
  assetId: yup.string().required('Please select an asset'),
  assetName: yup.string().required('Asset name is required'),
  sellValue: yup
    .number()
    .positive('Sell value must be positive')
    .required('Sell value is required'),
  deductions: yup
    .number()
    .min(0, 'Deductions cannot be negative')
    .required('Deductions are required'),
  finalPrice: yup
    .number()
    .positive('Final price must be positive')
    .required('Final price is required')
});

const SellIncomeAssets: React.FC<SellIncomeAssetsProps> = ({
  onBack,
  prefilledAsset,
  fromAssetList
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sellAssetLoading = useSelector(
    (state: RootState) => state.simulationDashboard.sellAssetLoading
  );
  const sellAssetError = useSelector(
    (state: RootState) => state.simulationDashboard.sellAssetError
  );
  const sellAssetResponse = useSelector(
    (state: RootState) => state.simulationDashboard.sellAssetResponse
  );

  const selectedProjectId = useAppSelector(
    (state) => state.homeTilesServices.selectedProjectId || ''
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  const [availableAssets, setAvailableAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [hasMoreAssets, setHasMoreAssets] = useState(false);
  const listboxRef = useRef<HTMLUListElement>(null);

  const getGroupId = () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    return selectedProject?.projectId || '';
  };

  // ✅ Fetch assets with pagination support
  const fetchAssets = async (token: string | null = null, append: boolean = false) => {
    setAssetsLoading(true);

    const payload: GetAllAssetsPayload = {
      groupId: selectedProjectId || getGroupId(),
      serviceId: 'fundManagementInvestment',
      unitType: 'incomeUnits',
      pageSize: 25,
      continuationToken: token
    };

    try {
      const result = await dispatch(getAllAssets(payload)).unwrap();

      const mappedAssets: Asset[] = result.assets.map((asset: any) => ({
        assetId: asset.assetId,
        assetName: asset.assetData?.identifier || 'Unknown Asset',
        assetType: asset.type,
        currentValue: parseFloat(asset.currentValue || asset.assetData?.currentValue || '0'),
        purchaseValue: parseFloat(asset.assetData?.purchaseValue || '0'),
        purchaseDate: asset.purchaseDate,
        subType: asset.assetData?.subType
      }));

      if (append) {
        setAvailableAssets((prev) => [...prev, ...mappedAssets]);
      } else {
        setAvailableAssets(mappedAssets);
      }

      // Store pagination info
      setContinuationToken(result.nextToken || null);
      setHasMoreAssets(result.hasMore || false);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load assets',
        severity: 'error'
      });
    } finally {
      setAssetsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAssets();
  }, [dispatch, selectedProjectId]);

  // ✅ Load more assets handler
  const handleLoadMore = () => {
    if (continuationToken && hasMoreAssets && !assetsLoading) {
      fetchAssets(continuationToken, true);
    }
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset
  } = useForm<SellAssetFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      assetId: prefilledAsset?.assetId || '',
      assetName: prefilledAsset?.assetName || '',
      sellValue: prefilledAsset?.currentValue || 0,
      deductions: 0,
      finalPrice: prefilledAsset?.currentValue || 0
    },
    mode: 'onChange'
  });

  const watchedValues = watch();

  // Auto-calculate final price when sell value or deductions change
  useEffect(() => {
    const sellValue = watchedValues.sellValue || 0;
    const deductions = watchedValues.deductions || 0;
    const finalPrice = sellValue - deductions;
    setValue('finalPrice', Math.max(finalPrice, 0));
  }, [watchedValues.sellValue, watchedValues.deductions, setValue]);

  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '$0';
    }

    const absAmount = Math.abs(amount);
    const isNegative = amount < 0;
    let formattedAmount;

    if (absAmount >= 1000000000) {
      formattedAmount = (absAmount / 1000000000).toFixed(3) + 'B';
    } else if (absAmount >= 1000000) {
      formattedAmount = (absAmount / 1000000).toFixed(3) + 'M';
    } else if (absAmount >= 1000) {
      formattedAmount = (absAmount / 1000).toFixed(3) + 'K';
    } else {
      formattedAmount = absAmount % 1 === 0 ? absAmount.toString() : absAmount.toFixed(2);
    }

    if (formattedAmount.includes('.')) {
      formattedAmount = formattedAmount.replace(/\.?0+$/, '');
    }

    const sign = isNegative ? '-' : '';
    return `${sign}$${formattedAmount}`;
  };

  const onSubmit = async (data: SellAssetFormData) => {
    const payload: SellAssetPayload = {
      groupId: selectedProjectId || getGroupId(),
      serviceId: 'fundManagementInvestment',
      unitType: 'incomeUnits',
      assetId: data.assetId,
      sellValue: parseFloat(data.sellValue.toFixed(2)),
      deductions: parseFloat(data.deductions.toFixed(2)),
      finalPrice: parseFloat(data.finalPrice.toFixed(2))
    };

    console.log('Submitting sell asset payload:', payload);

    try {
      const result = await dispatch(sellAsset(payload) as any);

      if (sellAsset.fulfilled.match(result)) {
        setSnackbar({
          open: true,
          message: 'Sell asset request submitted successfully!',
          severity: 'success'
        });

        setTimeout(() => {
          navigate('/fundManagementInvestment/request', {
            state: {
              from: 'assetSell',
              unitType: 'incomeUnits',
              isAdmin: true
            }
          });
          reset();
          dispatch(clearSellAssetResponse());
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: (result.error as any)?.message || 'Sell asset request submission failed',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Sell asset request submission failed:', error);
      setSnackbar({
        open: true,
        message: 'Sell asset request submission failed. Please try again.',
        severity: 'error'
      });
    }
  };

  const selectedAsset = useMemo(() => {
    return availableAssets.find((asset) => asset.assetId === watchedValues.assetId);
  }, [availableAssets, watchedValues.assetId]);

  if (assetsLoading && availableAssets.length === 0) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading assets...</Typography>
      </Container>
    );
  }

  if (!assetsLoading && availableAssets.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Assets Available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You don't have any income unit assets to sell.
          </Typography>
          <Button
            variant="contained"
            startIcon={<FaArrowLeft />}
            onClick={onBack}
            sx={{ textTransform: 'none' }}
          >
            Back to Selection
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<FaArrowLeft />}
          onClick={onBack}
          sx={{
            textTransform: 'none',
            color: '#666',
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
        >
          Back to Asset Type Selection
        </Button>
      </Box>

      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        Sell Income Unit Asset
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Submit a request to sell an asset from your income units portfolio
      </Typography>

      {prefilledAsset && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<FaCheckCircle />}>
          Asset pre-selected from your portfolio: <strong>{prefilledAsset.assetName}</strong>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ✅ LEFT Side - Transaction Summary */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Transaction Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {selectedAsset ? (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Selected Asset
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {selectedAsset.assetName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Type: {selectedAsset.assetType} | Subtype: {selectedAsset.subType}
                  </Typography>
                  {selectedAsset.purchaseDate && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Purchased: {new Date(selectedAsset.purchaseDate).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Purchase Value
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatCurrency(selectedAsset.purchaseValue || 0)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Current Value
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatCurrency(selectedAsset.currentValue)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Sell Value
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatCurrency(watchedValues.sellValue || 0)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography variant="body2" color="error">
                    Deductions
                  </Typography>
                  <Typography variant="body2" color="error" fontWeight="500">
                    - {formatCurrency(watchedValues.deductions || 0)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    bgcolor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body1" fontWeight="600">
                    Final Price
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(watchedValues.finalPrice || 0)}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                Please select an asset to see the summary
              </Typography>
            )}

            <Alert severity="info" sx={{ mt: 3 }}>
              Your sell request will be reviewed by the fund manager before processing.
            </Alert>
          </Paper>
        </Grid>

        {/* ✅ RIGHT Side - Asset Sell Details Form */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Asset Sell Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {/* Asset Selection with Load More */}
                <Grid item xs={12}>
                  <Controller
                    name="assetId"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={availableAssets}
                        getOptionLabel={(option) => option.assetName}
                        value={
                          availableAssets.find((asset) => asset.assetId === field.value) || null
                        }
                        onChange={(_, newValue) => {
                          field.onChange(newValue?.assetId || '');
                          setValue('assetName', newValue?.assetName || '');
                          if (newValue) {
                            setValue('sellValue', newValue.currentValue);
                          }
                        }}
                        loading={assetsLoading}
                        ListboxProps={{
                          ref: listboxRef,
                          style: { maxHeight: '400px' }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Asset"
                            error={!!errors.assetId}
                            helperText={errors.assetId?.message}
                            required
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {assetsLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option, state) => {
                          const isLastItem = state.index === availableAssets.length - 1;

                          return (
                            <React.Fragment key={option.assetId}>
                              <Box component="li" {...props}>
                                <Box sx={{ width: '100%' }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Typography variant="body1">{option.assetName}</Typography>
                                    <Typography variant="body2" color="primary" fontWeight="600">
                                      {formatCurrency(option.currentValue)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Type: {option.assetType} | Subtype: {option.subType}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* ✅ Load More button at the end */}
                              {isLastItem && hasMoreAssets && (
                                <ListSubheader
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 1,
                                    bgcolor: 'background.paper',
                                    borderTop: '1px solid',
                                    borderColor: 'divider'
                                  }}
                                >
                                  <Button
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLoadMore();
                                    }}
                                    disabled={assetsLoading}
                                    startIcon={
                                      assetsLoading ? <CircularProgress size={16} /> : null
                                    }
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {assetsLoading ? 'Loading...' : 'Load More Assets'}
                                  </Button>
                                </ListSubheader>
                              )}
                            </React.Fragment>
                          );
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Sell Value */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="sellValue"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Sell Value"
                        type="number"
                        error={!!errors.sellValue}
                        helperText={errors.sellValue?.message || 'Enter the gross sell value'}
                        inputProps={{
                          step: '0.01',
                          min: 0
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaDollarSign />
                            </InputAdornment>
                          )
                        }}
                        disabled={sellAssetLoading}
                        size={isMobile ? 'small' : 'medium'}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Deductions */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="deductions"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Deductions"
                        type="number"
                        error={!!errors.deductions}
                        helperText={
                          errors.deductions?.message || 'Fees, taxes, and other deductions'
                        }
                        inputProps={{
                          step: '0.01',
                          min: 0
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaDollarSign />
                            </InputAdornment>
                          )
                        }}
                        disabled={sellAssetLoading}
                        size={isMobile ? 'small' : 'medium'}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Final Price (Read-only) */}
                <Grid item xs={12}>
                  <Controller
                    name="finalPrice"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Final Price"
                        type="number"
                        error={!!errors.finalPrice}
                        helperText={
                          errors.finalPrice?.message ||
                          'Net amount after deductions (auto-calculated)'
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaDollarSign />
                            </InputAdornment>
                          ),
                          readOnly: true
                        }}
                        disabled
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            color: theme.palette.primary.main
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={onBack}
                      disabled={sellAssetLoading}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!isValid || sellAssetLoading}
                      startIcon={
                        sellAssetLoading ? <CircularProgress size={20} /> : <FaCheckCircle />
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: 150
                      }}
                    >
                      {sellAssetLoading ? 'Submitting...' : 'Submit Sell Request'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>

      {/* Success/Error Response */}
      {sellAssetResponse && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="success" icon={<FaCheckCircle />}>
            <Typography variant="subtitle2" fontWeight="600">
              Sell Request Submitted Successfully!
            </Typography>
            <Typography variant="body2">Request ID: {sellAssetResponse.assetSellId}</Typography>
            <Typography variant="body2">{sellAssetResponse.message}</Typography>
          </Alert>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SellIncomeAssets;
